import React, { useState, useEffect } from 'react';
import {
    Phone, PhoneOff, Volume2, UserCircle, Clock, Activity, RefreshCcw, Radio
} from 'lucide-react';
import Card from '../../components/ui/card';
import Badge from '../../components/ui/badge';
import Button from '../../components/ui/button';
import { useActiveCalls } from '../../hooks/useVoiceAPI';
import { formatDuration, formatPhoneNumber } from '../../lib/utils';

const LiveCallMonitor = ({ profileId, showTest = false }) => {
    const { activeCalls, loading, refetch, terminateCall, transferCall } = useActiveCalls(profileId, showTest);
    const [selectedCall, setSelectedCall] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [initialLoad, setInitialLoad] = useState(true);

    // Effect to handle initial loading state
    useEffect(() => {
        if (!loading) {
            setInitialLoad(false);
        }
    }, [loading]);

    // Default to first call if none selected
    const currentCall = activeCalls?.find(call => call.id === selectedCall) || activeCalls?.[0];

    // Timer Logic
    useEffect(() => {
        if (!currentCall?.created_at) {
            setElapsedTime(0);
            return;
        }
        const updateTimer = () => {
            const start = new Date(currentCall.created_at).getTime();
            const now = new Date().getTime();
            const seconds = Math.floor((now - start) / 1000);
            setElapsedTime(seconds > 0 ? seconds : 0);
        };
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [currentCall?.created_at]);

    // Helpers
    const getSentimentVariant = (sentiment) => {
        switch (sentiment?.toLowerCase()) {
            case 'positive': return 'success';
            case 'neutral': return 'info';
            case 'negative': return 'warning';
            case 'angry': return 'danger';
            default: return 'secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ringing': return <Phone size={16} style={{ color: 'var(--accent)' }} className="animate-pulse" />;
            case 'in-progress': return <Activity size={16} style={{ color: 'var(--success)' }} />;
            case 'on-hold': return <Volume2 size={16} style={{ color: 'var(--warning)' }} />;
            default: return <Phone size={16} style={{ color: 'var(--text-muted)' }} />;
        }
    };

    // --- RENDER STATES ---

    // ONLY show full spinner on INITIAL load
    if (initialLoad && loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}>
                <div style={{ width: '2rem', height: '2rem', border: '4px solid var(--border)', borderTop: '4px solid var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (!activeCalls || activeCalls.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '28rem',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px dashed var(--border-strong)',
                    backgroundColor: 'var(--surface-sunken)',
                    padding: '2rem'
                }}
            >
                <div style={{
                    padding: '1.5rem',
                    borderRadius: 'var(--radius-pill)',
                    backgroundColor: 'var(--accent-soft)',
                    marginBottom: '1.25rem',
                    border: '1px solid var(--accent-border)'
                }}>
                    <PhoneOff size={44} style={{ color: 'var(--accent-ink)' }} />
                </div>
                <p style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-display)',
                    margin: 0
                }}>
                    No Active Calls
                </p>
                <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--text-muted)',
                    marginTop: '0.5rem',
                    maxWidth: '24rem',
                    textAlign: 'center'
                }}>
                    The voice engine is ready and waiting for incoming traffic. Calls will appear here in real-time.
                </p>
                <Button
                    variant="primary"
                    size="md"
                    onClick={async () => {
                        setIsRefreshing(true);
                        if (refetch) await refetch();
                        setTimeout(() => setIsRefreshing(false), 500);
                    }}
                    disabled={isRefreshing}
                    style={{ marginTop: '1.5rem' }}
                    icon={<RefreshCcw size={15} className={isRefreshing ? "animate-spin" : ""} />}
                >
                    {isRefreshing ? 'Checking System...' : 'Refresh Status'}
                </Button>
                {/* Visual indicator that polling is active without blocking UI */}
                <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    color: 'var(--success)',
                    backgroundColor: 'var(--success-soft)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: 'var(--radius-pill)',
                    border: '1px solid var(--success-border)'
                }}>
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--success)' }}></span>
                        <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: 'var(--success)' }}></span>
                    </span>
                    System Online & Monitoring
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-12rem)] min-h-[600px]">
            {/* Active Calls List (Sidebar) */}
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
                <div className="flex items-center justify-between p-1">
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--success)' }}></span>
                            <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: 'var(--success)' }}></span>
                        </span>
                        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text)' }}>
                            Active Lines ({activeCalls.length})
                        </h3>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                            setIsRefreshing(true);
                            if (refetch) await refetch();
                            setTimeout(() => setIsRefreshing(false), 500);
                        }}
                        disabled={isRefreshing}
                        title="Refresh Active Calls"
                    >
                        <RefreshCcw size={14} className={isRefreshing ? "animate-spin" : ""} />
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                    {activeCalls.map((call) => {
                        const isSelected = selectedCall === call.id;
                        return (
                            <div
                                key={call.id}
                                onClick={() => setSelectedCall(call.id)}
                                style={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: 'var(--radius-lg)',
                                    padding: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    border: isSelected ? '1px solid var(--accent)' : '1px solid var(--border)',
                                    backgroundColor: isSelected ? 'var(--accent-soft)' : 'var(--surface-raised)',
                                    boxShadow: isSelected ? 'var(--shadow-sm)' : 'none'
                                }}
                            >
                                {/* Active Indicator Strip */}
                                {isSelected && (
                                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: 'var(--accent)' }} />
                                )}

                                <div className="flex items-start justify-between mb-3 pl-2">
                                    <div className="flex items-center gap-2.5">
                                        <div style={{ padding: '0.5rem', borderRadius: 'var(--radius-pill)', backgroundColor: isSelected ? 'var(--surface-raised)' : 'var(--surface-sunken)' }}>
                                            {getStatusIcon(call.status)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm" style={{ color: 'var(--text)' }}>
                                                {formatPhoneNumber(call.caller_number || call.callerNumber)}
                                            </p>
                                            <div className="flex items-center gap-1.5 text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                                <UserCircle size={12} />
                                                <span>Unknown Caller</span>
                                            </div>
                                        </div>
                                    </div>
                                    <Badge variant={getSentimentVariant(call.sentiment)}>
                                        {call.sentiment || 'NEUTRAL'}
                                    </Badge>
                                </div>

                                <div className="flex items-center justify-between pl-2 text-xs">
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ color: 'var(--text-muted)', backgroundColor: 'var(--surface-sunken)' }}>
                                        <Clock size={12} />
                                        <span className="font-mono font-medium">
                                            {call.id === currentCall?.id ? formatDuration(elapsedTime) : 'Active'}
                                        </span>
                                    </div>
                                    <span style={{ color: 'var(--text-muted)' }}>{new Date(call.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Call Details and Transcript (Main) */}
            <div className="col-span-12 lg:col-span-8 h-full flex flex-col">
                <Card className="h-full flex flex-col" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--surface-raised)', boxShadow: 'var(--shadow-md)' }}>
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--surface-sunken)' }}>
                        <div className="flex items-center gap-4">
                            <div style={{ height: '3rem', width: '3rem', borderRadius: 'var(--radius-pill)', backgroundColor: 'var(--accent-soft)', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--accent-border)' }}>
                                <Phone size={24} className="animate-pulse" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
                                    {formatPhoneNumber(currentCall.caller_number || currentCall.callerNumber)}
                                </h3>
                                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                                    <Radio size={14} style={{ color: 'var(--danger)' }} className="animate-pulse" />
                                    <span>Live Voice Session</span>
                                    <span className="mx-2">•</span>
                                    <span>{formatDuration(elapsedTime)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Badge variant={getSentimentVariant(currentCall.sentiment)}>
                                {currentCall.sentiment || 'NEUTRAL'}
                            </Badge>
                            <Badge variant="secondary" style={{ textTransform: 'uppercase' }}>
                                {currentCall.status}
                            </Badge>
                        </div>
                    </div>

                    {/* Real-time Transcript */}
                    <div className="flex-1 overflow-hidden relative" style={{ backgroundColor: 'var(--surface)' }}>
                        <div className="h-full p-6 overflow-y-auto space-y-6">
                            {(currentCall.transcript_json?.messages || currentCall.transcript || []).map((message, index) => (
                                <div
                                    key={index}
                                    className={`flex w-full ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                                >
                                    <div
                                        style={{
                                            maxWidth: '80%',
                                            borderRadius: 'var(--radius-lg)',
                                            padding: '1rem',
                                            boxShadow: 'var(--shadow-sm)',
                                            backgroundColor: message.role === 'assistant' ? 'var(--surface-raised)' : 'var(--accent-soft)',
                                            color: message.role === 'assistant' ? 'var(--text)' : 'var(--accent-ink)',
                                            border: message.role === 'assistant' ? '1px solid var(--border)' : '1px solid var(--accent-border)',
                                            borderTopLeftRadius: message.role === 'assistant' ? 0 : 'var(--radius-lg)',
                                            borderTopRightRadius: message.role === 'assistant' ? 'var(--radius-lg)' : 0
                                        }}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5 opacity-70">
                                            <span style={{ fontSize: '0.625rem', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.05em' }}>
                                                {message.role === 'assistant' ? 'Sofia (AI Assistant)' : 'Caller'}
                                            </span>
                                            <span style={{ fontSize: '0.625rem' }}>
                                                {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
                                            </span>
                                        </div>
                                        <p style={{ fontSize: '0.875rem', lineHeight: '1.625', margin: 0, whiteSpace: 'pre-wrap' }}>{message.content}</p>
                                    </div>
                                </div>
                            ))}
                            {(!currentCall.transcript_json?.messages && !currentCall.transcript) && (
                                <div className="flex flex-col items-center justify-center py-12 gap-3" style={{ color: 'var(--text-muted)' }}>
                                    <div style={{ width: '4rem', height: '0.25rem', backgroundColor: 'var(--border)', borderRadius: 'var(--radius-pill)' }} className="animate-pulse" />
                                    <div style={{ width: '2.5rem', height: '0.25rem', backgroundColor: 'var(--border)', borderRadius: 'var(--radius-pill)' }} className="animate-pulse delay-75" />
                                    <p className="text-sm pt-2">Listening for speech...</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="p-4 border-t flex justify-between items-center gap-3" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--surface-raised)' }}>
                        <Button
                            variant="outline"
                            size="md"
                            onClick={() => window.open(`/api/v1/voice/listen/${currentCall?.id}`, '_blank')}
                            icon={<Volume2 size={16} />}
                        >
                            Listen Live
                        </Button>

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="md"
                                onClick={() => {
                                    const phone = prompt("Enter number to transfer to:", "+1");
                                    if (phone && currentCall?.id) transferCall(currentCall.id, phone);
                                }}
                            >
                                Transfer Call
                            </Button>
                            <Button
                                variant="danger"
                                size="md"
                                onClick={() => currentCall?.id && terminateCall(currentCall.id)}
                                icon={<PhoneOff size={16} />}
                            >
                                Force Disconnect
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default LiveCallMonitor;
